import React from 'react';
import { Badge, type BadgeTone } from '../ui/Badge';
import { featureRegister } from '../../data/platform';
import type { BlockingDependency, FeatureStatus } from '../../types/platform';
const LABELS: Record<FeatureStatus, string> = {
  live: 'Live',
  configured: 'Configured — not yet enabled',
  staged: 'Staged — not in production',
  disabled: 'Disabled',
  under_development: 'Under development'
};
const TONES: Record<FeatureStatus, BadgeTone> = {
  live: 'ok',
  configured: 'warn',
  staged: 'warn',
  disabled: 'neutral',
  under_development: 'neutral'
};
const DEPENDENCIES: Record<BlockingDependency, string> = {
  none: '',
  licensed_provider: 'Waiting on a licensed provider',
  regulatory_approval: 'Waiting on regulatory approval',
  institutional_agreement: 'Waiting on an institutional agreement',
  device_capability: 'Depends on the device',
  administrator_configuration: 'Waiting on administrator configuration',
  engineering: 'Waiting on engineering'
};
export function statusLabel(status: FeatureStatus): string {
  return LABELS[status];
}
export function dependencyLabel(dependency: BlockingDependency): string {
  return DEPENDENCIES[dependency];
}

/** Read a capability's real status. Never assume it from context. */
export function featureFor(key: string) {
  return featureRegister.find(item => item.key === key) ?? null;
}
export function isLive(key: string): boolean {
  return featureFor(key)?.status === 'live';
}
export function FeatureStatusBadge({
  status
}: {
  status: FeatureStatus;
}) {
  return <Badge tone={TONES[status]} plain={status === 'live' ? undefined : true} data-id="element-4102" data-mp="2u70i9lhtdca8">
      {LABELS[status]}
    </Badge>;
}

/**
 * The user-facing explanation for a capability that is present but not
 * operational. Rendered instead of a bare "coming soon", so the person learns
 * what is actually missing and who has to supply it.
 */
export function FeatureGate({
  featureKey,
  inline = false
}: {
  featureKey: string;
  /** Compact form for sitting beside a disabled control. */
  inline?: boolean;
}) {
  const feature = featureFor(featureKey);
  if (!feature || feature.status === 'live') return null;
  if (inline) {
    return <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1" data-id="element-4103" data-mp="2jl1z9c9u7sxi">
        <FeatureStatusBadge status={feature.status} data-id="element-4104" data-mp="2jixp4clchxos" />
        {feature.dependency !== 'none' && <span className="text-caption font-medium text-warn-700" data-id="element-4105" data-mp="2odv7ia4iljec">
            {DEPENDENCIES[feature.dependency]}
          </span>}
      </span>;
  }
  return <div className="rounded-card border border-warn-100 bg-warn-50 p-4" data-id="element-4106" data-mp="2y8dcdilo3kzq">
      <div className="flex flex-wrap items-center gap-2" data-id="element-4107" data-mp="338rnj9ikfb1z">
        <FeatureStatusBadge status={feature.status} data-id="element-4108" data-mp="3376u5n7zly5p" />
        {feature.dependency !== 'none' && <span className="text-caption font-medium text-warn-700" data-id="element-4109" data-mp="378a6w0uqne2p">
            {DEPENDENCIES[feature.dependency]}
          </span>}
      </div>
      <p className="mt-2 max-w-[62ch] text-body-sm text-ink" data-id="element-4110" data-mp="1cxd944hcx6p2">{feature.note}</p>
    </div>;
}